import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  List as ListIcon,
  Apps as ProductIcon,
  Search as SearchIcon,
  Code as VersionIcon,
} from "@mui/icons-material";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Container,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminInfoDisplay from "../../components/admin/AdminInfoDisplay";
import ProductFormDialog from "../../components/admin/ProductFormDialog";
import { deleteProduct, getProducts } from "../../services/productService";

const ProductManagementPage = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [orderBy, setOrderBy] = useState("name");
  const [order, setOrder] = useState("asc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const data = await getProducts();
      setProducts(data);
      setError("");
    } catch (err) {
      setError("Failed to fetch products. Please try again.");
      console.error("Error fetching products:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddProduct = () => {
    setCurrentProduct(null);
    setOpenDialog(true);
  };

  const handleEditProduct = (product) => {
    setCurrentProduct(product);
    setOpenDialog(true);
  };

  const handleDeleteProduct = async (productId) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      try {
        await deleteProduct(productId);
        setProducts(products.filter((product) => product.id !== productId));
        showSuccessMessage("Product deleted successfully");
      } catch (err) {
        setError("Failed to delete product. Please try again.");
        console.error("Error deleting product:", err);
      }
    }
  };

  const handleProductSaved = (savedProduct, isNew) => {
    if (isNew) {
      setProducts([...products, savedProduct]);
      showSuccessMessage("Product added successfully");
    } else {
      setProducts(
        products.map((p) => (p.id === savedProduct.id ? savedProduct : p)),
      );
      showSuccessMessage("Product updated successfully");
    }
    setOpenDialog(false);
  };

  const showSuccessMessage = (message) => {
    setSuccessMessage(message);
    setOpenSnackbar(true);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredProducts = products
    .filter(
      (product) =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (product.description &&
          product.description
            .toLowerCase()
            .includes(searchTerm.toLowerCase())) ||
        (product.creator?.email &&
          product.creator.email
            .toLowerCase()
            .includes(searchTerm.toLowerCase())),
    )
    .sort((a, b) => {
      const isAsc = order === "asc";
      if (orderBy === "name") {
        return isAsc
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      }
      if (orderBy === "version") {
        const aVersion =
          a.ProductVersions?.find((v) => v.is_latest)?.version || "";
        const bVersion =
          b.ProductVersions?.find((v) => v.is_latest)?.version || "";
        return isAsc
          ? aVersion.localeCompare(bVersion)
          : bVersion.localeCompare(aVersion);
      }
      if (orderBy === "creator") {
        const aCreator = a.creator?.email || "";
        const bCreator = b.creator?.email || "";
        return isAsc
          ? aCreator.localeCompare(bCreator)
          : bCreator.localeCompare(aCreator);
      }
      return 0;
    });

  const paginatedProducts = filteredProducts.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: { xs: 2, sm: 3 },
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 1, sm: 0 },
        }}
      >
        <Button
          color="primary"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{
            mr: { sm: 2 },
            mb: { xs: 1, sm: 0 },
            alignSelf: { xs: "flex-start", sm: "center" },
          }}
          size={window.innerWidth < 600 ? "small" : "medium"}
        >
          Back
        </Button>
      </Box>
      <Card
        elevation={0}
        sx={{
          mb: 3,
          background: "transparent",
          border: "none",
          transition: "all 0.2s ease-in-out",
        }}
      >
        <CardHeader
          title={
            <Typography variant="h5" fontWeight="bold">
              Product Management
            </Typography>
          }
          subheader={
            <Typography variant="subtitle1" color="text.secondary">
              Manage products and their versions
            </Typography>
          }
          avatar={
            <Avatar sx={{ bgcolor: "primary.main", width: 48, height: 48 }}>
              <ProductIcon fontSize="large" />
            </Avatar>
          }
          action={
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddProduct}
            >
              Add Product
            </Button>
          }
        />
        <CardContent>
          <Grid
            container
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search products, descriptions, or creators..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" size="small">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={6}
              sx={{ display: "flex", justifyContent: "flex-end" }}
            >
              <Chip
                //  icon={<FilterIcon />}
                label={`${filteredProducts.length} products found`}
                color="primary"
                variant="outlined"
              />
            </Grid>
          </Grid>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TableContainer
            component={Paper}
            elevation={1}
            sx={{
              maxHeight: 600,
              transition: "all 0.2s ease-in-out",
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === "name"}
                      direction={orderBy === "name" ? order : "asc"}
                      onClick={() => handleSort("name")}
                    >
                      Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>License Required</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === "version"}
                      direction={orderBy === "version" ? order : "asc"}
                      onClick={() => handleSort("version")}
                    >
                      Latest Version
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === "creator"}
                      direction={orderBy === "creator" ? order : "asc"}
                      onClick={() => handleSort("creator")}
                    >
                      Administration
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedProducts.map((product, index) => (
                  <TableRow
                    key={product.id}
                    //  hover
                    sx={{
                      borderBottom: "1px solid",
                      borderBottomColor: "divider",
                      "&:hover": {
                        backgroundColor: "primary.main",
                        borderBottomColor: "primary.main",
                      },
                      "& td": {
                        py: 2,
                      },
                    }}
                  >
                    <TableCell sx={{ minWidth: 200 }}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {product.banner_image ? (
                          <Avatar
                            sx={{ mr: 2, width: 48, height: 48 }}
                            src={product.banner_image}
                            alt={`${product.name} banner`}
                          />
                        ) : (
                          <Avatar sx={{ mr: 2, bgcolor: "primary.main" }}>
                            <ProductIcon />
                          </Avatar>
                        )}
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {product.name}
                          </Typography>
                          {product.banner_image && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Has Banner
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 180 }}>
                      <Tooltip title={product.description || "No description"}>
                        <Typography noWrap sx={{ maxWidth: 300 }}>
                          {product.description || "No description"}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ minWidth: 180 }}>
                      <Chip
                        label={product.requires_license ? "Yes" : "No"}
                        color={
                          product.requires_license ? "secondary" : "default"
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 200 }}>
                      {product.ProductVersions?.find((v) => v.is_latest) ? (
                        <Chip
                          icon={<VersionIcon />}
                          label={
                            product.ProductVersions.find((v) => v.is_latest)
                              .version
                          }
                          color="primary"
                          variant="outlined"
                        />
                      ) : (
                        <Typography color="text.secondary">
                          No versions
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 200 }}>
                      {product.creator ? (
                        <Tooltip
                          title={
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: "bold" }}
                              >
                                Product Administration
                              </Typography>
                              <Typography variant="body2">
                                Created:{" "}
                                {new Date(
                                  product.created_at,
                                ).toLocaleDateString()}
                              </Typography>
                              {product.updated_at &&
                                product.updated_at !== product.created_at && (
                                  <Typography variant="body2">
                                    Updated:{" "}
                                    {new Date(
                                      product.updated_at,
                                    ).toLocaleDateString()}
                                  </Typography>
                                )}
                            </Box>
                          }
                          placement="left"
                        >
                          <Box>
                            <AdminInfoDisplay
                              admin={product.creator}
                              label="Created by"
                              size="small"
                            />
                            {product.updater &&
                              product.updater.id !== product.creator.id && (
                                <Box sx={{ mt: 0.5 }}>
                                  <AdminInfoDisplay
                                    admin={product.updater}
                                    label="Updated by"
                                    size="small"
                                  />
                                </Box>
                              )}
                          </Box>
                        </Tooltip>
                      ) : (
                        <Typography color="text.secondary" variant="caption">
                          Unknown
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit product">
                        <IconButton
                          color="primary"
                          onClick={() => handleEditProduct(product)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete product">
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteProduct(product.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Manage versions">
                        <IconButton
                          color="secondary"
                          onClick={() =>
                            navigate(`/admin/products/${product.id}/versions`)
                          }
                        >
                          <ListIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredProducts.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </CardContent>
      </Card>

      <ProductFormDialog
        open={openDialog}
        product={currentProduct}
        onClose={() => setOpenDialog(false)}
        onSave={handleProductSaved}
      />

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          sx={{ width: "100%" }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ProductManagementPage;
